import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Button, Tooltip } from '@sb/cc-design-system';
import { toString } from 'lodash';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from 'src/components/breadcrumbs';
import { FilterDropdownWithSearch } from 'src/components/filterDropdownWithSearch/FilterDropdownWithSearch';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { useNavigateBlockerContext } from 'src/components/navigateBlockerProvider';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Progress } from 'src/components/progress';
import { useParamQuery } from 'src/hooks';
import { validateTagName } from 'src/hooks/api/locationTags/helper';
import { useGetPageParamsString } from 'src/hooks/useGetPageParamsString';
import { ILocation, PublicationTypes } from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';

import { convertToViewLocationList } from './DetailLocationPage.helper';
import {
  ChannelGridContainer,
  ChannelsGrid,
  ChannelTileGridItem,
  Container,
  Content,
  Divider,
  InCardBreadcrumbs,
  InCardHomeButton,
  InCardNavbar,
  InCardNavbarActions,
  InCardNavbarLeft,
  InCardPublishButton,
  LOCATION_TAG_BADGE_SX,
  LOCATION_TAG_DROPDOWN_SELECT_PADDING,
  LocationName,
  LocationTagDropdownInputSx,
  OOTagChip,
  OOTagDropdownWrapper,
  OOTagLabel,
  OOTagList,
  PageCard,
  Section,
  SIDE_PANEL_CARD_BODY_STYLES,
  SIDE_PANEL_CARD_DESIGN,
  SidePanelCard,
  SidePanelScroll,
  SidePanelWrapper,
  StoreLocation,
  SubHeader,
  SubHeaderText,
} from './DetailLocationPageOO.styled';
import { useLocationEditPage } from './hooks/DetailLocationPage.hook';
import { ChannelOO } from './modules/Channel/ChannelOO';
import { OwnerOperatorTagsSectionOO } from './modules/OwnerOperatorTagsSection/OwnerOperatorTagsSectionOO';

export const DetailLocationPageOO = () => {
  const { locationCode = '' } = useParams();
  const searchString = useGetPageParamsString();
  const navigate = useNavigate();
  const { isViewOnly, getLocationsPath } = useUserRole();
  const { getParamsValue } = useParamQuery();
  const groupID = getParamsValue('groupID')[0];
  const preferUnassigned = !groupID || groupID === 'undefined';

  const {
    formMethods,
    channelsData,
    isLoadingLoaction,
    isLoadingChannels,
    locationOwnerTagIds,
    saveModal,
    publishModal,
    locationTagChanged,
    addLocationOwnerTag,
    createOwnerTag,
    ownerOperatorTags,
    onClickSave,
    navigateToLocationGroupEdit,
    publicationCloseModal,
    location,
    isBannerEnabled,
    isMessagingEnabled,
    setIsBannerEnabled,
    setIsMessagingEnabled,
    setIsBannerChanged,
    setIsMessagingChanged,
    isBannerChanged,
    isMessagingChanged,
    isContainsOutdoorType,
  } = useLocationEditPage(locationCode);
  const { setModalState, resetModalState } = useNavigateBlockerContext();
  const hasUnsavedChanges =
    locationTagChanged || isMessagingChanged || isBannerChanged;
  const [isLeaving, setIsLeaving] = useState(false);

  const { reset, state } = useBlocker(
    ({ nextLocation, currentLocation }) =>
      nextLocation.pathname !== currentLocation.pathname &&
      hasUnsavedChanges &&
      !isLeaving,
  );

  useEffect(() => {
    if (state === 'blocked') {
      const customProceed = () => {
        flushSync(() => {
          setIsLeaving(true);
        });

        reset();

        setTimeout(() => {
          navigate(
            {
              pathname: getLocationsPath({ preferUnassigned }),
              search: searchString,
            },
            { replace: true },
          );
        }, 0);
      };

      setModalState({
        isOpen: true,
        proceedNavigation: customProceed,
        resetNavigation: reset,
      });
    }

    return resetModalState;
  }, [
    reset,
    resetModalState,
    setModalState,
    state,
    navigate,
    getLocationsPath,
    preferUnassigned,
    searchString,
  ]);

  const selectedLocation = convertToViewLocationList(
    location ?? ({} as ILocation),
  );
  const locationName = toString(formMethods.getValues().location?.hq?.name);
  const ownerOperatorTagLookup = ownerOperatorTags.reduce<
    Record<string, string>
  >((acc, tag) => {
    acc[toString(tag.id)] = tag.name;

    return acc;
  }, {});
  const breadcrumbItems = [
    {
      label: lang.locations,
      link: getLocationsPath({ preferUnassigned }),
    },
    {
      label: locationName || locationCode,
    },
  ];

  return (
    <Container>
      <PageCard>
        <InCardNavbar>
          <InCardNavbarLeft>
            <InCardHomeButton
              size="small"
              onClick={() =>
                navigate({
                  pathname: getLocationsPath({ preferUnassigned }),
                  search: searchString,
                })
              }>
              <HomeOutlinedIcon fontSize="small" />
            </InCardHomeButton>
            <NavigateNextIcon fontSize="small" color="secondary" />
            <InCardBreadcrumbs>
              <Breadcrumbs
                items={breadcrumbItems}
                separator={
                  <NavigateNextIcon fontSize="small" color="secondary" />
                }
              />
            </InCardBreadcrumbs>
          </InCardNavbarLeft>
          {!isViewOnly && (
            <InCardNavbarActions>
              <InCardPublishButton
                variant="outlined-secondary-1"
                size="sm"
                onClick={publishModal.onOpenModal}>
                + {lang.publish}
              </InCardPublishButton>
              <Button
                variant="contained-primary"
                size="sm"
                disabled={!hasUnsavedChanges}
                onClick={saveModal.onOpenModal}>
                {lang.save}
              </Button>
            </InCardNavbarActions>
          )}
        </InCardNavbar>
        <Divider $marginTop={0} $marginBottom={0} />
        <Content container spacing={2}>
          <SidePanelWrapper item xs={12} sm={6} md={4} lg={3}>
            <SidePanelCard
              design={SIDE_PANEL_CARD_DESIGN}
              bodyStyles={SIDE_PANEL_CARD_BODY_STYLES}>
              <SidePanelScroll>
                <LocationName>{locationName || '-'}</LocationName>
                <Section container spacing={0}>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.nsn}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.store_number || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.storeNumber}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.store_number || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.storeAddress}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.address_line_1 || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.storeCity}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.city || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.storeZip}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.postal_code || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.locationCode}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.location_code || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.storeLocationZone}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.zone || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  <StoreLocation item xs={12}>
                    <SubHeader>{lang.storeLocationSubZone}</SubHeader>
                    <SubHeaderText>
                      {selectedLocation?.subzone || '-'}
                    </SubHeaderText>
                  </StoreLocation>
                  {!isLoadingLoaction && formMethods.getValues().location && (
                    <StoreLocation item xs={12}>
                      {isContainsOutdoorType && (
                        <OwnerOperatorTagsSectionOO
                          isBannerEnabled={isBannerEnabled}
                          isMessagingEnabled={isMessagingEnabled}
                          setIsBannerEnabled={setIsBannerEnabled}
                          setIsMessagingEnabled={setIsMessagingEnabled}
                          setIsBannerChanged={setIsBannerChanged}
                          setIsMessagingChanged={setIsMessagingChanged}
                          isBannerChanged={isBannerChanged}
                          isMessagingChanged={isMessagingChanged}
                        />
                      )}
                      <SubHeader>
                        {lang.ownerOperator.ownerOperatorTags}
                      </SubHeader>
                      <OOTagDropdownWrapper>
                        <FilterDropdownWithSearch
                          name={lang.ownerOperator.ownerOperatorTags}
                          placeholder={lang.addTag}
                          multiline={true}
                          onChange={(value: any) => {
                            addLocationOwnerTag(value);
                          }}
                          onNotExistedEntry={createOwnerTag}
                          inputValidator={validateTagName}
                          selectOptions={ownerOperatorTags.map((tag) => ({
                            value: tag.id,
                            label: tag.name,
                          }))}
                          value={locationOwnerTagIds}
                          badgeOptions={{
                            align: 'bottom',
                            color: 'default',
                            sx: LOCATION_TAG_BADGE_SX,
                          }}
                          inputFieldSx={LocationTagDropdownInputSx}
                          selectPadding={LOCATION_TAG_DROPDOWN_SELECT_PADDING}
                        />
                        <OOTagList>
                          {locationOwnerTagIds.map((tagId) => {
                            const normalizedTagId = toString(tagId);
                            const tagName =
                              ownerOperatorTagLookup[normalizedTagId] ??
                              normalizedTagId;

                            return (
                              <OOTagChip
                                key={normalizedTagId}
                                label={
                                  <Tooltip
                                    title={tagName}
                                    placement="top"
                                    showOnOverflowOnly>
                                    <OOTagLabel>{tagName}</OOTagLabel>
                                  </Tooltip>
                                }
                                size="small"
                                onDelete={() => {
                                  addLocationOwnerTag(
                                    locationOwnerTagIds
                                      .filter(
                                        (value) =>
                                          toString(value) !== normalizedTagId,
                                      )
                                      .map((value) => toString(value)),
                                  );
                                }}
                              />
                            );
                          })}
                        </OOTagList>
                      </OOTagDropdownWrapper>
                    </StoreLocation>
                  )}
                </Section>
              </SidePanelScroll>
            </SidePanelCard>
          </SidePanelWrapper>
          <ChannelGridContainer item xs={12} sm={6} md={8} lg={9}>
            {isLoadingChannels || isLoadingLoaction ? (
              <Progress />
            ) : channelsData.length === 0 ? (
              <NoResultAlert />
            ) : (
              <ChannelsGrid container columnSpacing={2} rowSpacing={0}>
                {channelsData.map((item, index) => (
                  <ChannelTileGridItem item xs={12} sm={6} lg={4} key={index}>
                    <ChannelOO
                      channelName={item.channel_name}
                      locationCode={locationCode}
                      locationGroup={item.locationGroup}
                      navigateToLocationGroupEdit={navigateToLocationGroupEdit}
                      layoutType={item.layoutType}
                      screens={item.screens}
                    />
                  </ChannelTileGridItem>
                ))}
              </ChannelsGrid>
            )}
          </ChannelGridContainer>
        </Content>
      </PageCard>
      <SimpleDialog
        open={saveModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.titleTextSave}
        subTitle={lang.subTitlePublishConfirm}
        onClose={saveModal.onCloseModal}
        onClickCancel={saveModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={onClickSave}
      />
      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          onClose={publicationCloseModal}
          publicationType={PublicationTypes.OVERRIDES}
          locationCodes={
            selectedLocation?.location_code
              ? [selectedLocation.location_code]
              : []
          }
        />
      )}
    </Container>
  );
};
